import { Euler, Shape, Vector3 } from 'three';
import * as THREE from 'three';

/**
 * Informations sur les 4 côtés (front, back, left, right)
 * Utilisé par les platings et les soles
 */
export const SIDES_INFORMATIONS = [
  { key: 'front', label: 'Devant', isRotated: false },
  { key: 'back', label: 'Derière', isRotated: false },
  { key: 'left', label: 'Gauche', isRotated: true },
  { key: 'right', label: 'Droite', isRotated: true },
] as const;

export type SideKey = 'front' | 'back' | 'left' | 'right';

/**
 * Construit un map des éléments actifs par clé
 */
export const buildActiveMap = (
  elements: { key: string; isActive: boolean }[],
): { [key: string]: boolean } => {
  return elements.reduce((acc, el) => {
    acc[el.key] = el.isActive;
    return acc;
  }, {} as { [key: string]: boolean });
};

/**
 * Crée les settings d'extrusion pour les shapes 3D
 */
export const createExtrudeSettings = (depth: number) => ({
  curveSegments: 12,
  steps: 2,
  depth,
  bevelEnabled: false,
});

/**
 * Fabrications par défaut utilisées pour sélectionner un granit
 */
export const DEFAULT_FABRICATIONS = [
  { fabrication: 'FRENCH' },
  { fabrication: 'CHINA' },
  { fabrication: 'OTHER' },
  { fabrication: 'INDIA' },
] as const;

/**
 * Options de configuration pour le chargement de texture
 */
export interface TextureConfigOptions {
  repeatX?: number;
  repeatY?: number;
  offsetX?: number;
}

/**
 * Charge et configure une texture avec les paramètres par défaut
 */
export const loadAndConfigureTexture = (
  loader: THREE.TextureLoader,
  bucketUrl: string,
  imageName: string,
  configureHD: (texture: THREE.Texture) => void,
  options: TextureConfigOptions = {},
): THREE.Texture => {
  const { repeatX, repeatY, offsetX } = options;

  const texture = loader.load(
    `${bucketUrl}/${imageName}?t=${Date.now()}`,
    configureHD,
  );
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.MirroredRepeatWrapping;
  if (offsetX) {
    texture.offset.x = offsetX;
  }
  if (repeatX) {
    texture.repeat.x = repeatX;
  }
  if (repeatY) {
    texture.repeat.y = repeatY;
  }
  return texture;
};

/**
 * Crée les éléments par défaut pour les 4 côtés (platings, soles)
 */
export const createDefaultSidesElements = (
  texture: THREE.Texture | null,
  granit: any | null,
): {
  texture: THREE.Texture | null;
  granit: any | null;
  isActive: boolean;
  key: string;
}[] =>
  SIDES_INFORMATIONS.map((side) => ({
    texture,
    granit,
    isActive: true,
    key: side.key,
  }));

/**
 * Crée une forme rectangulaire (avec pente optionnelle)
 * Utilisé pour les platings et les soles
 */
export const createRectangleShape = (
  shape: Shape,
  length: number,
  frontHeight: number,
  backHeight?: number,
): void => {
  shape.moveTo(-length / 2, 0);
  shape.lineTo(length / 2, 0);
  shape.lineTo(length / 2, -(backHeight ?? frontHeight));
  shape.lineTo(-length / 2, -frontHeight);
};

/**
 * Vérifie si un vecteur est nul (x, y, z tous à 0)
 */
export const isVectorNul = (vector: Vector3): boolean => {
  if (vector.x === 0 && vector.y === 0 && vector.z === 0) return true;
  return false;
};

/**
 * Arrondit un nombre à 5 décimales
 */
export const aroundNumber = (numberToRound: number): number => {
  return parseFloat(numberToRound.toFixed(5));
};

/**
 * Calcule la longueur d'un élément en fonction du mode et de la clé
 */
export const getLengthWithKey = (
  mode: 'closoir' | 'double closoir' | 'tete',
  key: 'front' | 'back' | 'left' | 'right',
  depth: { front: number; back: number; left: number; right: number },
  length: number,
): number => {
  switch (mode) {
    case 'closoir': {
      if (key === 'front') return length;
      if (key === 'left' || key === 'right') return length - depth.front;
      if (key === 'back') return length - depth.right - depth.left;
      break;
    }
    case 'tete': {
      if (key === 'front' || key === 'back') return length;
      if (key === 'left' || key === 'right')
        return length - depth.front - depth.back;
      break;
    }
    case 'double closoir': {
      if (key === 'front' || key === 'back')
        return length - depth.right - depth.left;
      if (key === 'left' || key === 'right') return length;
      break;
    }
  }
  return 0;
};

/**
 * Vérifie si un élément personnalisé (plating, sole) est actif
 */
export const isCustomElementUse = (
  customElements: any[],
  key: 'front' | 'back' | 'left' | 'right',
): boolean => {
  const element = customElements.find((el) => el.key === key);

  if (!element) return false;
  return element.isActive;
};

/**
 * Change le statut d'un élément personnalisé (plating, sole)
 */
export const changeCustomElementStatus = (
  key: 'front' | 'back' | 'left' | 'right',
  customElements: any[],
  setCustomElements: (customElements: any[]) => void,
): void => {
  const mappedCustomElements = customElements.map((el) => {
    if (el.key === key) {
      return {
        ...el,
        isActive: !el.isActive,
      };
    }
    return el;
  });

  setCustomElements(mappedCustomElements);
};

/**
 * Calcule la boîte englobante d'un élément personnalisé (plating, sole)
 */
export const getBoxOfCustomElement = (customElement: any) => {
  const geometryThree = new THREE.ExtrudeGeometry(
    customElement.shape,
    customElement.extrudeSettings,
  );
  // @ts-ignore
  const geometry: number[] = geometryThree.attributes.position.array;
  const position = customElement.position;
  const rotation = new Euler(
    0,
    customElement.shapeDetails.isRotated ? 1.57 : 0,
    0,
    'XYZ',
  );

  const rotationMatrix = new THREE.Matrix4();
  rotationMatrix.makeRotationFromEuler(rotation);

  const translationMatrix = new THREE.Matrix4();
  translationMatrix.makeTranslation(position.x, position.y, position.z);

  const geometryWithoutPosition = geometry.slice();
  for (let i = 0; i < geometryWithoutPosition.length; i += 3) {
    const vertex = new THREE.Vector3(
      geometryWithoutPosition[i],
      geometryWithoutPosition[i + 1],
      geometryWithoutPosition[i + 2],
    );

    vertex.applyMatrix4(rotationMatrix);

    geometryWithoutPosition[i] = vertex.x;
    geometryWithoutPosition[i + 1] = vertex.y;
    geometryWithoutPosition[i + 2] = vertex.z;
  }

  const transformedGeometry = geometry.slice();
  for (let i = 0; i < transformedGeometry.length; i += 3) {
    const vertex = new THREE.Vector3(
      transformedGeometry[i],
      transformedGeometry[i + 1],
      transformedGeometry[i + 2],
    );

    vertex.applyMatrix4(rotationMatrix);
    vertex.applyMatrix4(translationMatrix);

    transformedGeometry[i] = vertex.x;
    transformedGeometry[i + 1] = vertex.y;
    transformedGeometry[i + 2] = vertex.z;
  }

  const box = new THREE.Box3();
  box.setFromArray(transformedGeometry);

  return { box, geometry: transformedGeometry, geometryWithoutPosition };
};

export const getWidthMonument = ({
  pieces,
  customElements,
}: {
  pieces: any[];
  customElements?: any[];
}) => {
  let backLeft: Vector3 | undefined = undefined;
  let backRight: Vector3 | undefined = undefined;
  let frontLeft: Vector3 | undefined = undefined;
  let frontRight: Vector3 | undefined = undefined;

  let minY: number | undefined = undefined;

  pieces.forEach((element) => {
    const elementGeometry = element.geometry.attributes['position'].array;
    for (let i = 0; i < elementGeometry.length; i += 3) {
      if (
        minY === undefined ||
        minY > aroundNumber(elementGeometry[i + 1] + element.position.y)
      ) {
        minY = aroundNumber(elementGeometry[i + 1] + element.position.y);
      }
    }
  });

  for (const el of customElements ?? []) {
    if (el.shape) {
      const { box } = getBoxOfCustomElement(el);

      if (!minY || box.min.y < minY) {
        minY = box.min.y;
      }
    }
  }

  pieces.forEach((element) => {
    const elementGeometry = element.geometry.attributes['position'].array;
    for (let i = 0; i < elementGeometry.length; i += 3) {
      const elementX = aroundNumber(
        elementGeometry[i + 0] + element.position.x,
      );
      const elementY = aroundNumber(
        elementGeometry[i + 1] + element.position.y,
      );
      const elementZ = aroundNumber(
        elementGeometry[i + 2] + element.position.z,
      );

      if (elementY === minY) {
        if (backLeft === undefined)
          backLeft = new Vector3(elementX, elementY, elementZ);
        if (backRight === undefined)
          backRight = new Vector3(elementX, elementY, elementZ);
        if (frontLeft === undefined)
          frontLeft = new Vector3(elementX, elementY, elementZ);
        if (frontRight === undefined)
          frontRight = new Vector3(elementX, elementY, elementZ);

        if (elementX < backLeft.x)
          backLeft = new Vector3(elementX, minY, backLeft.z);
        if (elementX < frontLeft.x)
          frontLeft = new Vector3(elementX, minY, frontLeft.z);
        if (elementX > backRight.x)
          backRight = new Vector3(elementX, minY, backRight.z);
        if (elementX > frontRight.x)
          frontRight = new Vector3(elementX, minY, frontRight.z);

        if (elementZ < backLeft.z)
          backLeft = new Vector3(backLeft.x, minY, elementZ);
        if (elementZ > frontLeft.z)
          frontLeft = new Vector3(frontLeft.x, minY, elementZ);
        if (elementZ < backRight.z)
          backRight = new Vector3(backRight.x, minY, elementZ);
        if (elementZ > frontRight.z)
          frontRight = new Vector3(frontRight.x, minY, elementZ);
      }
    }
  });

  for (const el of customElements ?? []) {
    if (el.shape) {
      const { geometry } = getBoxOfCustomElement(el);

      for (let i = 0; i < geometry.length; i += 3) {
        const elementX = aroundNumber(geometry[i + 0]);
        const elementY = aroundNumber(geometry[i + 1]);
        const elementZ = aroundNumber(geometry[i + 2]);

        if (
          parseFloat(elementY.toFixed(2)) === parseFloat((minY ?? 0).toFixed(2))
        ) {
          if (backLeft === undefined)
            backLeft = new Vector3(elementX, elementY, elementZ);
          if (backRight === undefined)
            backRight = new Vector3(elementX, elementY, elementZ);
          if (frontLeft === undefined)
            frontLeft = new Vector3(elementX, elementY, elementZ);
          if (frontRight === undefined)
            frontRight = new Vector3(elementX, elementY, elementZ);

          if (elementX < backLeft.x)
            backLeft = new Vector3(elementX, minY, backLeft.z);
          if (elementX < frontLeft.x)
            frontLeft = new Vector3(elementX, minY, frontLeft.z);
          if (elementX > backRight.x)
            backRight = new Vector3(elementX, minY, backRight.z);
          if (elementX > frontRight.x)
            frontRight = new Vector3(elementX, minY, frontRight.z);

          if (elementZ < backLeft.z)
            backLeft = new Vector3(backLeft.x, minY, elementZ);
          if (elementZ > frontLeft.z)
            frontLeft = new Vector3(frontLeft.x, minY, elementZ);
          if (elementZ < backRight.z)
            backRight = new Vector3(backRight.x, minY, elementZ);
          if (elementZ > frontRight.z)
            frontRight = new Vector3(frontRight.x, minY, elementZ);
        }
      }
    }
  }

  return {
    backLeft: backLeft ?? new Vector3(0, 0, 0),
    backRight: backRight ?? new Vector3(0, 0, 0),
    frontLeft: frontLeft ?? new Vector3(0, 0, 0),
    frontRight: frontRight ?? new Vector3(0, 0, 0),
  };
};
